import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';

import classNames from 'classnames';

import { Checkbox, Container, Loader } from 'semantic-ui-react';

import '../css/Class.less';
import '../css/CourseListing.less';

import { SatCoreComponent, SatCoreRegister } from '../SatCoreRegistry';

import { ELEMENT_TYPE } from '../managers/CourseManager';
import { VIEW_SELECTION } from '../managers/NavigationManager';

import ClassroomService from '../services/ClassroomService';
import CourseNavigationService from '../services/CourseNavigationService';
import CourseService from '../services/CourseService';

export default
@inject(
  'assignmentManager', 'courseDeliveryManager', 'courseManager',
  'navigationManager', 'productManager', 'userManager', 'classroomManager',
  'verticalCourseTOCManager'
)
@observer
class ClassDetails extends Component {
  skipRefresh = false;

  constructor(props) {
    super(props);

    this.state = {
      isLoadingClassDetails: true,
      scrollRestorePending: false
    };

    this.BreadCrumbsCourseDropdown = SatCoreComponent('BreadCrumbsCourseDropdown');
    this.CourseLayout = SatCoreComponent('CourseLayout');
    this.CourseListBanner = SatCoreComponent('CourseListBanner');
    this.VerticalCourseTOC = SatCoreComponent('VerticalCourseTOC');
  }

  async componentDidMount() {
    await this.initClassDetails();
    this.handleScrollRestorePendingIfApplicable();
  }

  async componentDidUpdate(_prevProps, _prevState) {
    const { isLoadingClassDetails, scrollRestorePending } = this.state;
    // Trigger scroll restoration when loading completes and scroll is pending
    if (!isLoadingClassDetails && scrollRestorePending) {
      await this.restoreCourseElementResourceScrollPosition();
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ scrollRestorePending: false });
    }
  }

  componentWillUnmount() {
    const { classroomManager, courseManager } = this.props;

    // Clear scroll position when leaving the course view
    if (courseManager.allowPreserveCourseElementResourceScrollPosition) {
      const classroomId = classroomManager.currentClassroomId;
      const courseId = courseManager.currentCourseId;
      const courseElementId = courseManager.currentElementId;

      // Clear the courseTreeLeafElementId when navigating away
      CourseNavigationService.saveCurrentCourseLocation(
        classroomId,
        courseId,
        courseElementId,
        null // Clear courseTreeLeafElementId
      );
    }
  }

  // eslint-disable-next-line react/sort-comp
  initClassDetails = async ({ 
    courseElementId = undefined,
    courseId = undefined,
    fromCourseSearch = false
  } = {}) => {
    this.setState({ isLoadingClassDetails: true });

    const {
      courseManager, navigationManager, productManager, userManager
    } = this.props;

    const urlParams = new URLSearchParams(window.location.search);
    const view = urlParams.get('view');
    if (view && !productManager.FROM_TEACHER_PRODUCTS_NAV) {
      navigationManager.setView(urlParams.get('view'));
    } else {
      navigationManager.setView(VIEW_SELECTION.BOOK);
    }

    const previewAsStudent = urlParams.get('previewAsStudent');
    if (previewAsStudent) {
      userManager.setActivePreviewPermissionId(userManager.studentPermissionId);
    }

    // ensure currentClassroomId is initialized
    ClassroomService.getCurrentClassroomId({ alsoUpdateObservableWithUrlParamIfNull: true });

    if (fromCourseSearch && courseId) {
      courseManager.setCurrentCourseId(courseId);
    } else if (urlParams.has('courseId')) {
      if (courseManager.currentCourseId === null) {
        courseManager.setCurrentCourseId(urlParams.get('courseId'));
      }
    }

    if (this.skipRefresh) {
      this.skipRefresh = false;
    } else if (productManager.isFromProduct) {
      await CourseService.initCourseDataProduct();
    } else if (!navigationManager.isFetchingCourseDataFromNavigateToPath) {
      // Skip if NavigationService.navigateToPath is already fetching to avoid a
      // duplicate GET. Otherwise, prefer the URL's (course) elementId over
      // potentially stale courseManager.currentElementId, which can be out of
      // sync with a just-completed navigation (e.g. a breadcrumb click that
      // landed us here) and cause the wrong element to render.
      const routerUrlCourseElementId = urlParams.get('elementId');

      const effectiveCourseElementId = courseElementId || (
        routerUrlCourseElementId && routerUrlCourseElementId !== ELEMENT_TYPE.ROOT ? routerUrlCourseElementId : undefined
      );

      const shouldLoadData = true;
      const force = true;
      const rebuild = true;
      await CourseService.initCourseData(shouldLoadData, force, rebuild, {
        courseElementId: effectiveCourseElementId,
        courseId
      });
    }

    setTimeout(() => {
      this.setState({ isLoadingClassDetails: false });
    }, 500);
  }

  handleOpenSearch = () => {
    this.skipRefresh = true;
  }

  handleToggleHiddenResources = (_event, data) => {
    const shouldShowHiddenResources = data.checked;
    const { courseDeliveryManager } = this.props;
    courseDeliveryManager.setShouldShowHiddenResources(shouldShowHiddenResources);
  }

  /**
   * Preserve the scroll position when a CourseTreeLeaf button is clicked.
   * Captures the element ID from the clicked leaf card and saves it to session storage.
   */
  preserveCourseElementResourceScrollPosition = async (event) => {
    const { classroomManager, courseManager } = this.props;

    // Only trigger on actual button interactions (click or keyboard Enter/Space)
    // Ignore if the event target is not a button or link
    if (!event?.target) {
      return;
    }

    const { target } = event;
    const isButtonClick = target.tagName === 'BUTTON' || target.tagName === 'A' || target.closest('button') || target.closest('a');

    if (!isButtonClick) {
      return;
    }

    // Get courseElement from event - find the leaf card element
    const leafCard = target.closest('.leaf-card-satcore');
    if (!leafCard) {
      return;
    }

    const courseTreeLeafElementId = leafCard.getAttribute('data-element-id');
    if (!courseTreeLeafElementId) {
      return;
    }

    await CourseNavigationService.saveCurrentCourseLocation(
      classroomManager.currentClassroomId,
      courseManager.currentCourseId,
      courseManager.currentElementId,
      courseTreeLeafElementId
    );
  }

  handleCourseTreeLeafButtonClick = async (event) => {
    const { courseManager } = this.props;
    if (courseManager.allowPreserveCourseElementResourceScrollPosition) {
      await this.preserveCourseElementResourceScrollPosition(event);
    }
  }

  /**
   * Centralized handler to set scrollRestorePending state flag.
   * Called via various areas as a callback when data will be refreshed.
   * Triggers componentDidUpdate to call restoreCourseElementResourceScrollPosition when ready.
   */
  handleScrollRestorePendingIfApplicable = () => {
    const { courseManager } = this.props;

    // Only set scroll restore pending if feature is enabled
    if (courseManager.allowPreserveCourseElementResourceScrollPosition) {
      this.setState({ scrollRestorePending: true });
    }
  }

  restoreCourseElementResourceScrollPosition = async () => {
    const { classroomManager, courseManager } = this.props;

    // Only restore scroll if feature is enabled
    if (!courseManager.allowPreserveCourseElementResourceScrollPosition) {
      return;
    }

    const classroomId = classroomManager.currentClassroomId;
    const savedData = await CourseNavigationService.getCurrentCourseLocation(classroomId);

    if (savedData?.courseTreeLeafElementId) {
      // Use requestAnimationFrame to ensure DOM is fully painted
      requestAnimationFrame(() => {
        const leafCard = document.querySelector(
          `.leaf-card-satcore[data-element-id="${savedData.courseTreeLeafElementId}"]`
        );

        if (leafCard) {
          leafCard.scrollIntoView?.({ behavior: 'smooth', block: 'center' });

          // Focus on the card after scrolling completes
          // Use setTimeout to wait for smooth scroll animation
          setTimeout(() => {
            leafCard.focus?.({ preventScroll: true });
          }, 500); // Wait for scroll animation to complete
        }

        // Clear the saved scroll position after scrolling
        CourseNavigationService.saveCurrentCourseLocation(
          classroomId,
          courseManager.currentCourseId,
          courseManager.currentElementId,
          null // Clear courseTreeLeafElementId
        );
      });
    }
  }

  renderVerticalCourseTOCView() {
    const { assignmentManager, history, t, verticalCourseTOCManager } = this.props;
    const { CourseLayout, CourseListBanner, VerticalCourseTOC } = this;
    const { isVerticalCourseTOCOpen } = verticalCourseTOCManager;
    const showBreadCrumbs = isVerticalCourseTOCOpen ? undefined : 'bottom';
    return (
      <Container className={classNames('class-detail', 'vertical-course-toc-active')} fluid>
        <VerticalCourseTOC history={history} />
        <div className='class-detail-vertical-course-toc-content'>
          <CourseListBanner
            allowCourseResourceFilterLauncherButtonIfApplicable={true}
            forceHideHeader={false}
            hideAddCourseButton={true}
            hideAddStudentButton={true}
            history={history}
            resetClassDetails={this.initClassDetails}
            showBreadCrumbs={showBreadCrumbs}
            showCourseSearchButton={assignmentManager.enableCourseSearch}
            title={t('BannerTitle', 'No Class Courses Banner Title')} />
          <CourseLayout
            currentElement={null}
            handleCourseTreeLeafButtonClick={this.handleCourseTreeLeafButtonClick}
            handleScrollRestorePendingIfApplicable={this.handleScrollRestorePendingIfApplicable}
            history={history} />
        </div>
      </Container>
    );
  }

  renderClassicCourseView() {
    const {
      assignmentManager, courseDeliveryManager, courseManager,
      history, productManager, t, userManager
    } = this.props;

    const { isLoadingClassDetails } = this.state;

    const { activePermissionId } = userManager;
    const { BreadCrumbsCourseDropdown, CourseLayout, CourseListBanner } = this;
    const isCustomCourse = courseManager.isCustomCourse(courseManager.currentCourseId);

    const { FROM_TEACHER_PRODUCTS_NAV } = productManager;

    // eslint-disable-next-line react/destructuring-assignment
    const zeroState = this.props.zerostate || (
      <div className='no-courses'>
        {!isCustomCourse ? t('coursesZeroStateMsg')
          : t('customAssessmentZeroStateMsg', 'There are no assessments added yet.')}
      </div>
    );

    // NOTE: we typically need `hideAddCourseButton` to be true here,
    // as we only want to display '+ Add Course' at the Course Level (ClassCourses.js).
    // ---
    // Instead, when ClassCourses is called, that calls its own instance of CourseListBanner, which will have `hideAddCourseButton=false`
    // ---
    // We currently hide the '+ Add Course' button from all other Course Tree levels (branch, leaf).
    let hideAddCourseButton = true;
    // The only case where this should currently be false is for an old (unused) DEMO-1787 feature (see TeacherProductView for more info)
    if (FROM_TEACHER_PRODUCTS_NAV) {
      hideAddCourseButton = false;
    }

    // TODO DEMO-2925: uncomment if we decide to hide 'Go to Search' button when in 'Product Course Preview' mode
    // const isFromProductCoursePreviewMode = productManager.isFromProduct === true;

    return courseManager.isTreeLoading || isLoadingClassDetails ? <Loader active /> : (
      <Container className={classNames('class-detail', {
        'teacher-product-class-detail': FROM_TEACHER_PRODUCTS_NAV
      })} fluid>
        <CourseListBanner
          allowCourseResourceFilterLauncherButtonIfApplicable={true}
          forceHideHeader={false}
          hideAddCourseButton={hideAddCourseButton}
          hideAddStudentButton={true}
          history={history}
          resetClassDetails={this.initClassDetails}
          showBreadCrumbs='bottom'
          showBreadCrumbsDropdown='bottom'
          showCourseSearchButton={assignmentManager.enableCourseSearch/* && !isFromProductCoursePreviewMode */}
          title={t('BannerTitle', 'No Class Courses Banner Title')} />
        {(courseManager.currentCourseId !== null
          && courseManager.currentCourseElementList.length > 0) ? (
            <Container
              className={classNames(`class-content resources-container class-details-inner-container ${activePermissionId}`, {
                'has-course-search-btn': assignmentManager.enableCourseSearch
              })} fluid>
              {!courseManager.isTreeLoading && (
                <div className='class-details-header'>
                  <div className='class-details-header-left'>
                    {/* marked as 'display: none' by default */}
                    <BreadCrumbsCourseDropdown className='top' history={history} />
                  </div>
                  <div className='class-details-header-mid'>
                    {/* placeholder */}
                  </div>
                  <div className='class-details-header-right'>
                    {!userManager.isStudent && courseDeliveryManager.showResourcePacingToolbar && (
                      <div className='toggle-hidden-resources-wrapper default-toggler-directly-on-background-wrapper'>
                        <div className='hidden-resources-toggler-label'>
                          {t('showHiddenResources', 'Show hidden resources')}
                        </div>
                        <Checkbox
                          className='hidden-resources-toggler'
                          defaultChecked={courseDeliveryManager.shouldShowHiddenResources}
                          onChange={this.handleToggleHiddenResources}
                          toggle />
                      </div>
                    )}
                  </div>
                </div>
              )}
              <CourseLayout
                currentElement={null}
                handleCourseTreeLeafButtonClick={this.handleCourseTreeLeafButtonClick}
                handleScrollRestorePendingIfApplicable={this.handleScrollRestorePendingIfApplicable}
                history={history} />
            </Container>
          ) : !courseManager.isTreeLoading && !courseManager.currentCourseElementList.length && !isLoadingClassDetails && (
            <Container className='class-content'>
              {zeroState}
            </Container>
          )}
      </Container>
    );
  }

  render() {
    const { verticalCourseTOCManager } = this.props;
    return (
      verticalCourseTOCManager.allowVerticalCourseTOC ? (
        this.renderVerticalCourseTOCView()
      ) : this.renderClassicCourseView()
    );
  }
}

SatCoreRegister('ClassDetails', ClassDetails);
